import { useEffect, useMemo, useState } from "react";
import { useListControls, ListControlsBar } from "@/components/ListSearchSort";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Search, Filter, Plus, BookOpen, Layers, Sparkles, User, Globe2, PenSquare } from "lucide-react";

/**
 * Drill + Routine library.
 * Three-tier visibility: Rally Lab / Coach-shared / My drafts.
 * Filter by sport, sub-skill, level range, free-text.
 */

const SUB_SKILL_OPTIONS = {
  pickleball: ["Serve", "Return", "Dink", "Third Shot Drop", "Volley", "Reset", "Footwork", "Kitchen Play", "Mental", "Fitness"],
  squash: ["Length", "Drives", "Boast", "Drop Shot", "Volley", "Lob", "Footwork", "T-Position", "Mental", "Fitness"],
};

const SOURCE_META = {
  rally_lab: { label: "Rally Lab", color: "#CCFF00", icon: Sparkles, desc: "Official" },
  coach_shared: { label: "Shared", color: "#00BFFF", icon: Globe2, desc: "Coach community" },
  coach_draft: { label: "My drafts", color: "#F59E0B", icon: PenSquare, desc: "Only visible to you" },
};

export default function DrillLibrary() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("drills");  // drills | routines
  const [drills, setDrills] = useState([]);
  const [routines, setRoutines] = useState([]);
  const [sport, setSport] = useState("pickleball");
  const [subSkill, setSubSkill] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = { sport };
      if (subSkill) params.sub_skill = subSkill;
      if (sourceFilter) params.source = sourceFilter;
      if (q) params.q = q;
      const [d, r] = await Promise.all([
        api.get("/drills", { params }),
        api.get("/routines", { params: { sport, ...(sourceFilter ? { source: sourceFilter } : {}), ...(q ? { q } : {}) } }),
      ]);
      setDrills(d.data || []);
      setRoutines(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load library");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [sport, subSkill, sourceFilter]);

  const items = tab === "drills" ? drills : routines;
  const ctl = useListControls(items, { name: (i) => i.title || i.name, initialSort: "default" });
  const isCoach = user?.role === "coach" || user?.role === "admin";

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white pt-14 pb-16">
      <div className="max-w-6xl mx-auto px-6 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="label-mono text-[#CCFF00] mb-2"><BookOpen className="w-3.5 h-3.5 inline mr-1" /> DRILL LIBRARY</div>
            <h1 className="h-display text-4xl sm:text-5xl tracking-tight">Practice-ready drills & routines</h1>
            <p className="text-sm text-[#A3A3A3] mt-2 max-w-2xl">
              Rally Lab official drills, community-shared routines, and your own drafts.
              Filter by skill, print for court-side reference, or clone to customise.
            </p>
          </div>
          {isCoach && (
            <div className="flex gap-2">
              <button onClick={() => nav("/drills/new")} className="btn-volt px-4 py-2 text-xs inline-flex items-center gap-1.5" data-testid="new-drill-btn">
                <Plus className="w-3.5 h-3.5" /> New drill
              </button>
              <button onClick={() => nav("/routines/new")} className="px-4 py-2 text-xs uppercase tracking-wider border border-[#CCFF00] text-[#CCFF00] hover:bg-[#CCFF00] hover:text-black inline-flex items-center gap-1.5" data-testid="new-routine-btn">
                <Layers className="w-3.5 h-3.5" /> New routine
              </button>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-[#262626]" data-testid="library-tabs">
          {[
            { k: "drills", label: `Drills (${drills.length})` },
            { k: "routines", label: `Routines (${routines.length})` },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              className={`px-4 py-2 text-xs uppercase tracking-wider ${tab === t.k ? "text-[#CCFF00] border-b-2 border-[#CCFF00]" : "text-[#A3A3A3] hover:text-white"}`}
              data-testid={`tab-${t.k}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Filters */}
        <div className="grid md:grid-cols-4 gap-2" data-testid="filters">
          <div>
            <div className="label-mono text-[10px] text-[#A3A3A3] mb-1">SPORT</div>
            <select value={sport} onChange={(e) => setSport(e.target.value)} className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-sm" data-testid="filter-sport">
              <option value="pickleball">Pickleball</option>
              <option value="squash">Squash</option>
            </select>
          </div>
          {tab === "drills" && (
            <div>
              <div className="label-mono text-[10px] text-[#A3A3A3] mb-1">SUB-SKILL</div>
              <select value={subSkill} onChange={(e) => setSubSkill(e.target.value)} className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-sm" data-testid="filter-sub-skill">
                <option value="">All skills</option>
                {SUB_SKILL_OPTIONS[sport].map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <div className="label-mono text-[10px] text-[#A3A3A3] mb-1">SOURCE</div>
            <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-sm" data-testid="filter-source">
              <option value="">All</option>
              <option value="rally_lab">Rally Lab official</option>
              <option value="coach_shared">Coach-shared</option>
              {isCoach && <option value="coach_draft">My drafts</option>}
            </select>
          </div>
          <div>
            <div className="label-mono text-[10px] text-[#A3A3A3] mb-1">SEARCH</div>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-[#A3A3A3]" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && load()}
                placeholder="dink, boast, third shot…"
                className="w-full bg-[#141414] border border-[#262626] pl-7 pr-3 py-2 text-sm"
                data-testid="filter-search"
              />
            </div>
          </div>
        </div>

        {loading && <div className="text-sm text-[#A3A3A3] italic">Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="card-surface p-8 text-center text-sm text-[#A3A3A3]" data-testid="empty-state">
            No {tab} match the current filters. {isCoach && `Try creating one with the "New ${tab.slice(0, -1)}" button above.`}
          </div>
        )}

        {/* List */}
        <ListControlsBar ctl={ctl} hideSearch modes={["default", "az", "za"]} testid="drills" />
        <div className="grid md:grid-cols-2 gap-3" data-testid={`${tab}-list`}>
          {ctl.list.map((item) => {
            const meta = SOURCE_META[item.source] || SOURCE_META.coach_draft;
            const Icon = meta.icon;
            const route = tab === "drills" ? `/drills/${item.id}` : `/routines/${item.id}`;
            return (
              <Link
                key={item.id}
                to={route}
                className="card-surface p-4 hover:border-[#CCFF00] transition-colors block"
                data-testid={`${tab.slice(0, -1)}-card-${item.id}`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-base text-white truncate">{item.title}</div>
                    {item.summary && <div className="text-xs text-[#A3A3A3] mt-1 line-clamp-2">{item.summary}</div>}
                    {tab === "routines" && (
                      <div className="text-[10px] text-[#A3A3A3] mt-1 label-mono">{(item.drills || []).length} drills · {item.context}</div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <span className="text-[9px] uppercase tracking-wider label-mono px-1.5 py-0.5" style={{ color: meta.color, borderColor: meta.color, borderWidth: 1 }}>
                      <Icon className="w-2.5 h-2.5 inline mr-0.5" /> {meta.label}
                    </span>
                    {tab === "drills" && (
                      <span className="text-[10px] text-[#A3A3A3] label-mono">
                        L{item.skill_level_min}-{item.skill_level_max} · {item.duration_min}m
                      </span>
                    )}
                  </div>
                </div>
                {tab === "drills" && (item.sub_skills || []).length > 0 && (
                  <div className="flex gap-1 flex-wrap mt-1">
                    {item.sub_skills.slice(0, 4).map((s) => (
                      <span key={s} className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-[#262626] text-[#A3A3A3]">{s}</span>
                    ))}
                  </div>
                )}
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
